/**
 * Turns router navigations into the page-by-page journey PostHog records.
 *
 * Pageviews are sent by hand rather than by the SDK's history listener,
 * because the raw URL is the wrong grain: every product route is prefixed
 * with `/$workspaceSlug`, so `acme/people` and `globex/people` are the same
 * screen under two URLs. The registered route template is carried alongside
 * the path as `route_id`, and that is what journeys and funnels group on.
 */
import type { AnyRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { capturePageView } from "@/shared/analytics/analytics";
import type { IntegrationConsent } from "@/shared/integrations/integration.types";

interface RouteMatchLike {
  readonly routeId: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

interface WorkspaceLike {
  readonly id: string;
  readonly slug: string;
}

export interface RouteSnapshot {
  /** URL pathname, no search or hash. */
  path: string;
  /** Registered template of the deepest match, e.g. `/app-layout/$workspaceSlug/people`. */
  routeId: string;
  /** `$workspaceSlug` from the matched params, when the route is workspace-scoped. */
  workspaceSlug: string | null;
}

/** The deepest match owns the page; parents are layouts. */
export function routeIdFromMatches(matches: ReadonlyArray<RouteMatchLike>): string {
  return matches.at(-1)?.routeId ?? "/";
}

/**
 * Reads `$workspaceSlug` from the innermost match that carries it. Child
 * matches inherit their parent's params, so this walks outward only to stay
 * correct if a future route shadows the param.
 */
export function workspaceSlugFromMatches(matches: ReadonlyArray<RouteMatchLike>): string | null {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const value = matches[i]?.params?.workspaceSlug;
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

/**
 * Maps a URL slug to a real workspace id.
 *
 * Deliberately not `useActiveWorkspace()`: that hook falls back to a MOCK
 * workspace for anonymous and unresolved sessions, which would report a
 * fabricated workspace id for every signed-out visitor. An unknown slug
 * yields null here instead.
 */
export function resolveWorkspaceId(
  slug: string | null,
  workspaces: ReadonlyArray<WorkspaceLike>,
): string | null {
  if (!slug) return null;
  return workspaces.find((workspace) => workspace.slug === slug)?.id ?? null;
}

/**
 * Null until the router has matched something.
 *
 * The first render can land before the initial match resolves, and a
 * smoke test caught what that produces: a real `$pageview` for the right
 * path under the root route template, immediately followed by the same page
 * under its actual one. Reporting nothing until there is a match is the fix,
 * the resolved navigation arrives a tick later either way.
 */
export function readRouteSnapshot(router: AnyRouter): RouteSnapshot | null {
  const matches = router.state.matches as ReadonlyArray<RouteMatchLike>;
  if (matches.length === 0) return null;
  return {
    path: router.state.location.pathname,
    routeId: routeIdFromMatches(matches),
    workspaceSlug: workspaceSlugFromMatches(matches),
  };
}

/** Current resolved location, refreshed on every completed navigation. */
export function useRouteSnapshot(router: AnyRouter): RouteSnapshot | null {
  const [snapshot, setSnapshot] = useState<RouteSnapshot | null>(() => readRouteSnapshot(router));

  useEffect(() => {
    // Read once on mount too: the first navigation can resolve before this
    // effect runs, so without it the landing page is missing from the journey.
    setSnapshot(readRouteSnapshot(router));
    return router.subscribe("onResolved", () => {
      setSnapshot(readRouteSnapshot(router));
    });
  }, [router]);

  return snapshot;
}

/** Sends one `$pageview` per distinct location. */
export function usePageView(
  snapshot: RouteSnapshot | null,
  workspaceId: string | null,
  consent: IntegrationConsent,
): void {
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    if (!snapshot || consent !== "granted") return;
    // StrictMode double-invokes effects in dev, and `onResolved` can fire
    // again for a location we already reported (a search-param-only update
    // that resolves to the same path). Both would double-count the page.
    const key = `${snapshot.path}|${snapshot.routeId}|${workspaceId ?? ""}`;
    if (lastSent.current === key) return;
    lastSent.current = key;

    capturePageView({
      path: snapshot.path,
      routeId: snapshot.routeId,
      workspaceId,
    });
  }, [snapshot, workspaceId, consent]);
}
